/* oxlint-disable eslint/max-lines */
/**
 * MyLibrarySubmenu Component (EVI-721)
 * Unified flat list of document collections and web source lists.
 * Uses existing atoms directly — no separate logic stack.
 *
 * TODO: The current popover-based submenu pattern won't scale as more
 * collections are added. My Library and Clinical Tools should eventually
 * become their own dedicated page/view rather than nested popovers to
 * better handle large lists and provide room for richer interactions.
 */

import { openMyLibrarySubmenuAtom } from '@/atoms/sourceControlAtoms';;
import { isSoleEnabledChatSourceBucket } from '@/atoms/chatSourceRequireOne';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { IS_UNIFIED_COLLECTIONS_ENABLED } from '@/features/flags/legacy-flags';
import { Checkbox } from '@heidi/design-system/checkbox';
import { SearchInput } from '@heidi/design-system/search-input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@heidi/design-system/popover';
import { PopoverMenuItem } from '@heidi/design-system/popover-menu-item';
import { Separator } from '@heidi/design-system/separator';
import { Switch } from '@heidi/design-system/switch';
import { TypographyCaption } from '@heidi/design-system/typography';
import { LastSourceRequiredTooltip } from '@/components/chat/components/source-control-v2/LastSourceRequiredTooltip';
import { useExpandedSourceControlTrackingEnabled } from '@/features/flags';
import { useCollections, useUnifiedCollections } from '@heidi/evidence/data';
import { isEvidenceEligibleCollection, isIdInAllowlist } from '@heidi/evidence/contracts';
import { useSourceLists } from '@heidi/evidence/data';
import { useTeamEvidenceEnforcement } from '@/hooks/useTeamEvidenceEnforcement';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ButtonAction } from '@heidi/runtime/analytics-vocabulary';
import { useAtom } from 'jotai';
import { ChevronRight, Folder, FolderOpen, LibraryBig, Lock, Plus, Stethoscope } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSourceControlActions } from '../hooks/useSourceControlActions';
import { myLibrarySubmenuMessages } from './my-library-submenu.messages';
import { track } from '@/lib/analytics/track';

interface MyLibrarySubmenuProps {
  disabled: boolean;
  onCreateCollectionClick?: () => void;
}

export const MyLibrarySubmenu = ({ disabled, onCreateCollectionClick }: MyLibrarySubmenuProps) => {
  const handleGoToLibrary = onCreateCollectionClick ?? (() => {});
  const intl = useIntl();
  const isUnifiedEnabled = IS_UNIFIED_COLLECTIONS_ENABLED();
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [openFromAtom, setOpenFromAtom] = useAtom(openMyLibrarySubmenuAtom);
  useEffect(() => {
    if (openFromAtom && !disabled) {
      setSubmenuOpen(true);
      setOpenFromAtom(false);
    }
  }, [openFromAtom, setOpenFromAtom, disabled]);
  const { baseProps } = useEvidenceAnalytics();

  const {
    toggleState,
    handleDocCollectionToggle,
    handleWebCollectionToggle,
    handleUnifiedCollectionToggle,
    handleUncollectedToggle,
    resetUncollected,
    isRequireAtLeastOneSource,
  } = useSourceControlActions(baseProps);

  const {
    enforcedLibraryCollectionIds,
    libraryCollectionAllowlistIds,
    libraryCollectionIdsChangeable,
    enforcedWebCollectionIds,
    webCollectionIdsChangeable,
    canCreateTeamCollection,
    teamName,
    isPerCollectionLibraryModesEnabled,
  } = useTeamEvidenceEnforcement();

  const hasLibraryEnforcement = libraryCollectionAllowlistIds != null;
  const hasWebEnforcement = enforcedWebCollectionIds != null;
  // Hide "Not in a collection" when selection is locked OR an allowlist is enforced.
  // Gating on allowlist alone left the toggle interactive when changeable=false
  // with an empty allowlist (EVI-1956), which mass-selects all libraries.
  const showUncollectedToggle =
    libraryCollectionIdsChangeable && !hasLibraryEnforcement;

  useEffect(() => {
    if (hasLibraryEnforcement || !libraryCollectionIdsChangeable) {
      resetUncollected();
    }
  }, [hasLibraryEnforcement, libraryCollectionIdsChangeable, resetUncollected]);

  const { data: collectionsData } = useCollections();
  const allCollections = useMemo(() => collectionsData?.collections ?? [], [collectionsData]);
  const { data: sourceLists } = useSourceLists();
  const allWebCollections = useMemo(() => sourceLists ?? [], [sourceLists]);

  const { allCollections: unifiedCollections } = useUnifiedCollections();

  const evidenceUnifiedCollections = useMemo(
    () => unifiedCollections.filter(isEvidenceEligibleCollection),
    [unifiedCollections]
  );

  const collections = useMemo(
    () => {
      const evidenceCollections = allCollections.filter(isEvidenceEligibleCollection);
      return hasLibraryEnforcement
        ? evidenceCollections.filter((c) =>
            isIdInAllowlist(
              c.collection_id,
              libraryCollectionAllowlistIds,
              c.legacy_source_list_id
            )
          )
        : evidenceCollections;
    },
    [allCollections, libraryCollectionAllowlistIds, hasLibraryEnforcement]
  );

  const enforcedLibraryIdSet = useMemo(
    () => new Set(enforcedLibraryCollectionIds ?? []),
    [enforcedLibraryCollectionIds]
  );

  const isLibraryCollectionLocked = (collectionId: string) => {
    if (isPerCollectionLibraryModesEnabled) {
      return enforcedLibraryIdSet.has(collectionId);
    }
    return !libraryCollectionIdsChangeable;
  };

  const webCollections = useMemo(
    () =>
      hasWebEnforcement
        ? allWebCollections.filter((c) =>
            isIdInAllowlist(c.id, enforcedWebCollectionIds)
          )
        : allWebCollections,
    [allWebCollections, enforcedWebCollectionIds, hasWebEnforcement]
  );

  const isAnySelected = toggleState.myLibrary || toggleState.webCollections;

  const isExpandedSourceTrackingEnabled = useExpandedSourceControlTrackingEnabled();

  const handleSubmenuOpenChange = useCallback(
    (open: boolean) => {
      if (disabled) return;
      setSubmenuOpen(open);
      if (!open) setSearchQuery('');
      if (open && isExpandedSourceTrackingEnabled) {
        track({
          event_name: TrackingEventName.ButtonClicked,
          props: {
            ...baseProps,
            action: ButtonAction.EvidenceSourcesListClicked,
            label: 'My library submenu',
            feature: 'evidence',
          },
        });
      }
    },
    [disabled, isExpandedSourceTrackingEnabled, baseProps]
  );

  const allSelectedIds = useMemo(
    () => new Set([...toggleState.selectedCollectionIds, ...toggleState.selectedWebCollectionIds]),
    [toggleState.selectedCollectionIds, toggleState.selectedWebCollectionIds]
  );

  const getSummary = () => {
    if (!isAnySelected) return null;

    if (isUnifiedEnabled) {
      const selectedNames = evidenceUnifiedCollections
        .filter((c) => allSelectedIds.has(c.id))
        .map((c) => c.name);
      const count = selectedNames.length;
      if (count === 0) return null;
      if (count === 1) return selectedNames[0]!;
      return intl.formatMessage(myLibrarySubmenuMessages.selectedCount, { count });
    }

    const selectedNames: string[] = [];

    if (toggleState.includeUncollected) {
      selectedNames.push(intl.formatMessage(myLibrarySubmenuMessages.uncollectedDocuments));
    }
    if (toggleState.myLibrary) {
      for (const id of toggleState.selectedCollectionIds) {
        const col = collections.find((c) => c.collection_id === id);
        if (col) selectedNames.push(col.name);
      }
    }

    if (toggleState.webCollections) {
      for (const id of toggleState.selectedWebCollectionIds) {
        const wc = webCollections.find((c) => c.id === id);
        if (wc) selectedNames.push(wc.name);
      }
    }

    const count = selectedNames.length;
    if (count === 0) return null;
    if (count === 1) return selectedNames[0]!;
    return intl.formatMessage(myLibrarySubmenuMessages.selectedCount, { count });
  };

  const hasNoContent = isUnifiedEnabled
    ? evidenceUnifiedCollections.length === 0 &&
      !hasLibraryEnforcement &&
      !hasWebEnforcement
    : collections.length === 0 &&
      webCollections.length === 0 &&
      !hasLibraryEnforcement &&
      !hasWebEnforcement &&
      !showUncollectedToggle;

  const query = searchQuery.toLowerCase();
  const filteredCollections = query
    ? collections.filter((c) => c.name.toLowerCase().includes(query))
    : collections;
  const filteredWebCollections = query
    ? webCollections.filter((c) => c.name.toLowerCase().includes(query))
    : webCollections;
  const visibleUnified = evidenceUnifiedCollections.filter((c) => {
    if (c._source === 'library') {
      return (
        !hasLibraryEnforcement ||
        isIdInAllowlist(c.id, libraryCollectionAllowlistIds, c._sourceListId)
      );
    }
    return (
      !hasWebEnforcement ||
      isIdInAllowlist(c.id, enforcedWebCollectionIds, c._sourceListId)
    );
  });
  const filteredUnified = query
    ? visibleUnified.filter((c) => c.name.toLowerCase().includes(query))
    : visibleUnified;
  const hasNoResults = isUnifiedEnabled
    ? query && filteredUnified.length === 0
    : query && filteredCollections.length === 0 && filteredWebCollections.length === 0;

  const lockLastLibrarySource =
    isRequireAtLeastOneSource && isSoleEnabledChatSourceBucket(toggleState, 'library');
  const lockLastWebCollectionSource =
    isRequireAtLeastOneSource &&
    isSoleEnabledChatSourceBucket(toggleState, 'webCollections');
  const lockLastUncollectedSwitch =
    lockLastLibrarySource &&
    toggleState.includeUncollected &&
    toggleState.selectedCollectionIds.length === 0;

  const isLastSelectedDocCollection = (collectionId: string) =>
    lockLastLibrarySource &&
    !toggleState.includeUncollected &&
    toggleState.selectedCollectionIds.length === 1 &&
    toggleState.selectedCollectionIds[0] === collectionId;

  const isLastSelectedWebCollection = (collectionId: string) =>
    lockLastWebCollectionSource &&
    toggleState.selectedWebCollectionIds.length === 1 &&
    toggleState.selectedWebCollectionIds[0] === collectionId;

  const isLastSelectedUnifiedCollection = (collectionId: string) => {
    if (!isRequireAtLeastOneSource || !allSelectedIds.has(collectionId)) return false;
    if (allSelectedIds.size !== 1) return false;
    // Deselecting this id clears the only remaining library/web selection.
    return (
      isSoleEnabledChatSourceBucket(toggleState, 'library') ||
      isSoleEnabledChatSourceBucket(toggleState, 'webCollections')
    );
  };

  return (
    <Popover open={submenuOpen} onOpenChange={handleSubmenuOpenChange}>
      <PopoverTrigger asChild disabled={disabled}>
        <PopoverMenuItem
          disabled={disabled}
          data-testid="source-my-library-trigger"
          className="data-[state=open]:bg-surface-hover"
          aria-haspopup="true"
          aria-expanded={submenuOpen}
          aria-label={intl.formatMessage(myLibrarySubmenuMessages.myLibraryMenuLabel)}
          icon={<LibraryBig className="size-4" />}
          accessoryEnd={
            <span className="flex items-center gap-1">
              {!disabled && isAnySelected && (() => {
                const summary = getSummary();
                if (!summary) return null;
                // The accessory never shrinks, so the cap is what leaves the
                // label room; `sm:` tracks the popover's `sm:min-w-96`.
                return (
                  <TypographyCaption
                    className="max-w-20 truncate text-text-tertiary sm:max-w-40"
                    title={summary}
                  >
                    {summary}
                  </TypographyCaption>
                );
              })()}
              <ChevronRight className="size-4 text-text-secondary rtl:-scale-x-100" />
            </span>
          }
        >
          {intl.formatMessage(myLibrarySubmenuMessages.myLibrary)}
        </PopoverMenuItem>
      </PopoverTrigger>

      <PopoverContent
        side="right"
        align="end"
        sideOffset={4}
        collisionPadding={16}
        className="w-64 p-1"
      >
        <div className="flex flex-col">
          {hasNoContent ? (
            <PopoverMenuItem
              onClick={handleGoToLibrary}
              data-testid="source-create-collection-trigger"
              icon={<Plus className="size-4 text-text-secondary" />}
            >
              {intl.formatMessage(myLibrarySubmenuMessages.goToLibrary)}
            </PopoverMenuItem>
          ) : (
            <>
              <div className="p-1">
                <SearchInput
                  placeholder={intl.formatMessage(myLibrarySubmenuMessages.librarySearchPlaceholder)}
                  clearAriaLabel={intl.formatMessage({
                    defaultMessage: 'Clear search',
                    id: '4YJHutR26p',
                  })}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="h-[270px] overflow-y-auto">
                {isUnifiedEnabled ? (
                  filteredUnified.map((collection) => {
                    const isSelected = allSelectedIds.has(collection.id);
                    const isTeamLocked = collection._source === 'library'
                      ? isLibraryCollectionLocked(collection.id)
                      : !webCollectionIdsChangeable;
                    const lastSourceLocked = isLastSelectedUnifiedCollection(collection.id);
                    const isLocked = isTeamLocked || lastSourceLocked;
                    const docLabel = collection.document_count > 0
                      ? intl.formatMessage(myLibrarySubmenuMessages.documentCount, { count: collection.document_count })
                      : null;
                    const domainLabel = collection.domains.length > 0
                      ? collection.domains.length <= 2 && collection.document_count === 0
                        ? collection.domains.join(', ')
                        : intl.formatMessage(myLibrarySubmenuMessages.domainCount, { count: collection.domains.length })
                      : null;
                    const description = docLabel && domainLabel
                      ? `${docLabel}, ${domainLabel}`
                      : docLabel ?? domainLabel ?? '';
                    const unifiedTrackingContext = {
                      name: collection.name,
                      sources:
                        collection._source === 'web'
                          ? collection.domains
                          : undefined,
                      source_ids:
                        collection._source === 'web'
                          ? collection.domains
                          : undefined,
                      sources_count:
                        collection._source === 'web'
                          ? collection.domains.length
                          : collection.document_count,
                    };
                    const checkbox = (
                      <Checkbox
                        checked={isSelected}
                        disabled={lastSourceLocked}
                        onCheckedChange={() =>
                          handleUnifiedCollectionToggle(
                            collection.id,
                            collection._source,
                            unifiedTrackingContext
                          )
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                    );
                    return (
                      <PopoverMenuItem
                        key={collection.id}
                        data-testid="source-library-collection"
                        title={collection.name}
                        onClick={
                          isLocked
                            ? undefined
                            : () =>
                                handleUnifiedCollectionToggle(
                                  collection.id,
                                  collection._source,
                                  unifiedTrackingContext
                                )
                        }
                        icon={<Folder className="size-4 text-text-secondary" />}
                        description={description}
                        accessoryEnd={
                          isTeamLocked ? (
                            <Lock className="size-3.5 text-text-tertiary" />
                          ) : lastSourceLocked ? (
                            <LastSourceRequiredTooltip>{checkbox}</LastSourceRequiredTooltip>
                          ) : (
                            checkbox
                          )
                        }
                        selected={isSelected}
                        disabled={isLocked}
                      >
                        {collection.name}
                      </PopoverMenuItem>
                    );
                  })
                ) : (
                  <>
                    {showUncollectedToggle && (
                      <PopoverMenuItem
                        onClick={
                          lockLastUncollectedSwitch
                            ? undefined
                            : () => handleUncollectedToggle()
                        }
                        icon={toggleState.includeUncollected
                          ? <FolderOpen className="size-4 text-text-secondary" />
                          : <Folder className="size-4 text-text-secondary" />
                        }
                        description={intl.formatMessage(myLibrarySubmenuMessages.uncollectedDocumentsDescription)}
                        accessoryEnd={
                          lockLastUncollectedSwitch ? (
                            <LastSourceRequiredTooltip>
                              <Switch
                                size="xs"
                                checked={toggleState.includeUncollected}
                                disabled
                                onClick={(e) => e.stopPropagation()}
                              />
                            </LastSourceRequiredTooltip>
                          ) : (
                            <Switch
                              size="xs"
                              checked={toggleState.includeUncollected}
                              onCheckedChange={() => handleUncollectedToggle()}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )
                        }
                        selected={toggleState.includeUncollected}
                        disabled={lockLastUncollectedSwitch}
                      >
                        {intl.formatMessage(myLibrarySubmenuMessages.uncollectedDocuments)}
                      </PopoverMenuItem>
                    )}

                    {filteredCollections.map((collection) => {
                      const isSelected =
                        toggleState.selectedCollectionIds.includes(collection.collection_id);
                      const isTeamLocked = isLibraryCollectionLocked(
                        collection.collection_id
                      );
                      const lastSourceLocked = isLastSelectedDocCollection(
                        collection.collection_id
                      );
                      const isLocked = isTeamLocked || lastSourceLocked;
                      const docTrackingContext = {
                        name: collection.name,
                        source_ids: [collection.collection_id],
                        sources_count: collection.document_count,
                      };
                      const checkbox = (
                        <Checkbox
                          checked={isSelected}
                          disabled={lastSourceLocked}
                          onCheckedChange={() =>
                            handleDocCollectionToggle(
                              collection.collection_id,
                              docTrackingContext
                            )
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      );
                      return (
                        <PopoverMenuItem
                          key={collection.collection_id}
                          data-testid="source-library-collection"
                          title={collection.name}
                          onClick={
                            isLocked
                              ? undefined
                              : () =>
                                  handleDocCollectionToggle(
                                    collection.collection_id,
                                    docTrackingContext
                                  )
                          }
                          icon={<Folder className="size-4 text-text-secondary" />}
                          description={intl.formatMessage(myLibrarySubmenuMessages.documentCount, { count: collection.document_count })}
                          accessoryEnd={
                            isTeamLocked ? (
                              <Lock className="size-3.5 text-text-tertiary" />
                            ) : lastSourceLocked ? (
                              <LastSourceRequiredTooltip>{checkbox}</LastSourceRequiredTooltip>
                            ) : (
                              checkbox
                            )
                          }
                          selected={isSelected}
                          disabled={isLocked}
                        >
                          {collection.name}
                        </PopoverMenuItem>
                      );
                    })}

                    {filteredWebCollections.map((collection) => {
                      const isSelected = toggleState.selectedWebCollectionIds.includes(
                        collection.id
                      );
                      const isTeamLocked = !webCollectionIdsChangeable;
                      const lastSourceLocked = isLastSelectedWebCollection(collection.id);
                      const isLocked = isTeamLocked || lastSourceLocked;
                      const domainDescription =
                        collection.domains.length <= 2
                          ? collection.domains.join(', ')
                          : intl.formatMessage(myLibrarySubmenuMessages.domainCount, { count: collection.domains.length });
                      const webTrackingContext = {
                        name: collection.name,
                        sources: collection.domains,
                        source_ids: collection.domains,
                        sources_count: collection.domains.length,
                      };
                      const checkbox = (
                        <Checkbox
                          checked={isSelected}
                          disabled={lastSourceLocked}
                          onCheckedChange={() =>
                            handleWebCollectionToggle(collection.id, webTrackingContext)
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      );
                      return (
                        <PopoverMenuItem
                          key={collection.id}
                          data-testid="source-library-collection"
                          title={collection.name}
                          onClick={
                            isLocked
                              ? undefined
                              : () =>
                                  handleWebCollectionToggle(
                                    collection.id,
                                    webTrackingContext
                                  )
                          }
                          icon={<Stethoscope className="size-4 text-text-secondary" />}
                          description={domainDescription}
                          accessoryEnd={
                            isTeamLocked ? (
                              <Lock className="size-3.5 text-text-tertiary" />
                            ) : lastSourceLocked ? (
                              <LastSourceRequiredTooltip>{checkbox}</LastSourceRequiredTooltip>
                            ) : (
                              checkbox
                            )
                          }
                          selected={isSelected}
                          disabled={isLocked}
                        >
                          {collection.name}
                        </PopoverMenuItem>
                      );
                    })}
                  </>
                )}

                {hasNoResults && (
                  <div className="p-2">
                    <TypographyCaption className="text-text-tertiary">
                      {intl.formatMessage(myLibrarySubmenuMessages.noResults)}
                    </TypographyCaption>
                  </div>
                )}
              </div>

              {canCreateTeamCollection && (
                <>
                  <PopoverMenuItem
                    onClick={handleGoToLibrary}
                    data-testid="source-create-collection-trigger"
                    icon={<Plus className="size-4 text-text-secondary" />}
                  >
                    {intl.formatMessage(myLibrarySubmenuMessages.goToLibrary)}
                  </PopoverMenuItem>
                </>
              )}

              {(hasLibraryEnforcement || hasWebEnforcement) && (
                <>
                  <Separator className="my-1" />
                  <div className="flex items-center gap-1.5 px-3 py-2">
                    <Lock className="size-3 shrink-0 text-text-tertiary" />
                    <TypographyCaption className="text-text-tertiary">
                      {intl.formatMessage(myLibrarySubmenuMessages.managedByTeamAdmin, { teamName: teamName ?? 'team' })}
                    </TypographyCaption>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
